import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Polyline,
  Marker,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import * as turf from "@turf/turf";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { FcProcess } from "react-icons/fc";
import { MdMyLocation } from "react-icons/md";
import L from "leaflet";
import "../App.css";
import "./Pages.css";
import Modal from "../Modal";
import Logo from "../Logo";

const libraries = ["places"];

const customMarkerIcon = new L.Icon({
  iconUrl: "/marker-icon.png",
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

function Calculator() {
  const [startCountyData, setStartCountyData] = useState(null);
  const [endCountyData, setEndCountyData] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [startAddress, setStartAddress] = useState("");
  const [endAddress, setEndAddress] = useState("");
  const [totalMileage, setTotalMileage] = useState(null);
  const [outOfCountyMiles, setOutOfCountyMiles] = useState(null);
  const [error, setError] = useState("");
  const [mapBounds, setMapBounds] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [startCountyName, setStartCountyName] = useState("");
  const [endCountyName, setEndCountyName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const openRouteServiceApiKey = process.env.REACT_APP_OPENROUTESERVICE_API_KEY;

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const fetchCountyData = async (lat, lon, setData, setName) => {
    try {
      if (!lat || !lon) throw new Error("Invalid coordinates");
      const response = await axios.get(
        `https://public.opendatasoft.com/api/records/1.0/search/?dataset=us-county-boundaries&rows=1&geofilter.distance=${lat},${lon},10000`
      );
      if (response.data.records.length === 0) throw new Error("No county data found");
      const fields = response.data.records[0].fields;
      setData(fields.geo_shape);
      setName(fields.county || fields.county_name || fields.name);
    } catch (err) {
      console.error("Error fetching county data:", err);
      setError("Unable to fetch county data for one of the addresses.");
    }
  };

  const geocodeAddress = async (address) => {
    try {
      setError("");
      const geoResponse = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );
      if (geoResponse.data.status !== "OK") throw new Error("Error geocoding");
      const location = geoResponse.data.results[0].geometry.location;
      return { lat: location.lat, lon: location.lng };
    } catch (err) {
      console.error("Error geocoding:", err);
      setError("Unable to geocode one of the addresses. Please check and try again.");
      return null;
    }
  };

  const getCurrentLocation = async (setter) => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const response = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
        );
        if (response.data.status === "OK") setter(response.data.results[0].formatted_address);
        else setError("Unable to get address for current location.");
      },
      (err) => setError("Geolocation error: " + err.message)
    );
  };

  const fetchRouteData = async (startCoords, endCoords) => {
    try {
      const orsResponse = await axios.post(
        `https://api.openrouteservice.org/v2/directions/driving-car/geojson`,
        { coordinates: [[startCoords.lon, startCoords.lat], [endCoords.lon, endCoords.lat]] },
        { headers: { Authorization: `Bearer ${openRouteServiceApiKey}` } }
      );
      const coords = orsResponse.data.features[0].geometry.coordinates;
      const distance = turf.length(turf.lineString(coords), { units: "miles" }).toFixed(2);
      return { routeCoordinates: coords, distance };
    } catch (err) {
      console.error("Route error:", err.message);
      setError("No route found between the addresses.");
      return { routeCoordinates: [], distance: 0 };
    }
  };

  const calculateOutOfCountyMiles = (routeCoordinates, countyBoundary) => {
    let out = 0;
    const countyPolygon = turf.polygon(countyBoundary.coordinates);
    for (let i = 0; i < routeCoordinates.length - 1; i++) {
      const seg = turf.lineString([routeCoordinates[i], routeCoordinates[i + 1]]);
      const startIn = turf.booleanPointInPolygon(turf.point(routeCoordinates[i]), countyPolygon);
      const endIn = turf.booleanPointInPolygon(turf.point(routeCoordinates[i + 1]), countyPolygon);
      if (startIn && !endIn) {
        const inter = turf.lineIntersect(countyPolygon, seg);
        if (inter.features.length > 0) {
          out += turf.length(turf.lineString([inter.features[0].geometry.coordinates, routeCoordinates[i + 1]]), { units: "miles" });
        }
      } else if (!startIn && !endIn) {
        out += turf.length(seg, { units: "miles" });
      }
    }
    return out.toFixed(2);
  };

  useEffect(() => {
    if (startCountyData && routeCoordinates.length > 0) {
      setOutOfCountyMiles(calculateOutOfCountyMiles(routeCoordinates, startCountyData));
    }
  }, [startCountyData, routeCoordinates]);

  const handleCalculate = async () => {
    try {
      setIsProcessing(true);
      setStartCountyData(null);
      setEndCountyData(null);
      setRouteCoordinates([]);
      setMapBounds(null);
      setError("");

      const startCoords = await geocodeAddress(startAddress);
      const endCoords = await geocodeAddress(endAddress);

      if (!startCoords || !endCoords) {
        if (startAddress.trim().toLowerCase() === "a" && endAddress.trim().toLowerCase() === "cow") {
          setIsModalOpen(true);
        } else {
          setError("Unable to geocode one of the addresses.");
        }
        setIsProcessing(false);
        return;
      }

      await fetchCountyData(startCoords.lat, startCoords.lon, setStartCountyData, setStartCountyName);
      await fetchCountyData(endCoords.lat, endCoords.lon, setEndCountyData, setEndCountyName);

      const { routeCoordinates: coords, distance } = await fetchRouteData(startCoords, endCoords);
      setTotalMileage(distance);
      setRouteCoordinates(coords);

      if (coords.length > 0) {
        setMapBounds(turf.bbox(turf.lineString(coords)));
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred. Please check the input and try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const MapView = () => {
    const map = useMap();
    useEffect(() => {
      if (mapBounds) {
        const bounds = [
          [mapBounds[1], mapBounds[0]],
          [mapBounds[3], mapBounds[2]],
        ];
        map.fitBounds(bounds);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mapBounds from parent state, fit when it changes
    }, [map, mapBounds]);
    return null;
  };

  const handlePlaceSelect = (addressSetter) => (place) => {
    if (place.formatted_address) addressSetter(place.formatted_address);
  };

  const onLoad = (autocomplete, addressSetter) => {
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      handlePlaceSelect(addressSetter)(place);
    });
  };

  return (
    <div className="app-container calculator-page">
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        message="Lauren, you are the sweetest person created on this planet, I hope you never forget how limitless you are. I adore you"
      />

      <article className="page-content calculator-intro">
        <h1>Out of County Mileage Calculator</h1>
        <p>
          Enter your <strong>start address</strong> and <strong>end address</strong> below. CountyLee will show total mileage, starting and ending counties, and how many miles were driven <strong>out of your starting county</strong>. Use this for reimbursement, payroll, or record-keeping. This tool is for record-keeping purposes only; confirm reimbursement rates with your employer or accountant.
        </p>
        <h2>Example</h2>
        <p>
          Driving 120 miles for an out-of-county call might result in 85 out-of-county miles. At $0.70 per mile (example rate), that could be $59.50 for the out-of-county portion. Your actual reimbursement depends on your employer&apos;s policy.
        </p>
      </article>

      <div className="input-container">
        <div className="logo-container">
          <Logo />
        </div>
        <p className="subtitle">Enter addresses to calculate</p>
        {isLoaded ? (
          <>
            <div className="input-wrapper">
              <Autocomplete onLoad={(ac) => onLoad(ac, setStartAddress)}>
                <input
                  type="text"
                  value={startAddress}
                  onChange={(e) => setStartAddress(e.target.value)}
                  placeholder="Enter start address"
                  className="input-field"
                  aria-label="Start address"
                />
              </Autocomplete>
              <button
                type="button"
                className="mylocation-button"
                onClick={() => getCurrentLocation(setStartAddress)}
                aria-label="Use current location for start"
              >
                <MdMyLocation />
              </button>
            </div>
            <div className="input-wrapper">
              <Autocomplete onLoad={(ac) => onLoad(ac, setEndAddress)}>
                <input
                  type="text"
                  value={endAddress}
                  onChange={(e) => setEndAddress(e.target.value)}
                  placeholder="Enter end address"
                  className="input-field"
                  aria-label="End address"
                />
              </Autocomplete>
              <button
                type="button"
                className="mylocation-button"
                onClick={() => getCurrentLocation(setEndAddress)}
                aria-label="Use current location for end"
              >
                <MdMyLocation />
              </button>
            </div>
          </>
        ) : (
          <p>Loading map services…</p>
        )}
        <button
          type="button"
          onClick={handleCalculate}
          className="action-button"
          disabled={isProcessing}
        >
          {isProcessing ? (
            <><FcProcess style={{ marginRight: "8px", display: "inline-block" }} /> Calculating...</>
          ) : (
            "Calculate Out of County Miles"
          )}
        </button>

        {(outOfCountyMiles || totalMileage || startCountyName || endCountyName) && (
          <div className="results-container">
            {outOfCountyMiles && (
              <div className="result-card primary-result">
                <h3>Out of County Miles</h3>
                <p className="result-value">{outOfCountyMiles} miles</p>
              </div>
            )}
            {totalMileage && (
              <div className="result-card">
                <p><strong>Total Mileage:</strong> {totalMileage} miles</p>
              </div>
            )}
            {startCountyName && (
              <div className="result-card">
                <p><strong>Starting County:</strong> {startCountyName}</p>
              </div>
            )}
            {endCountyName && (
              <div className="result-card">
                <p><strong>Ending County:</strong> {endCountyName}</p>
              </div>
            )}
            <button type="button" onClick={() => setShowMap(!showMap)} className="toggle-map-button">
              {showMap ? "Hide Map" : "Show Map Visualization"}
            </button>
          </div>
        )}
        {error && <p className="error-text">{error}</p>}
      </div>

      {showMap && (outOfCountyMiles || totalMileage) && (
        <div className="map-container">
          <MapContainer center={[35.758, -79.019]} zoom={7} className="map">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {routeCoordinates.length > 0 && (
              <>
                <Marker position={[routeCoordinates[0][1], routeCoordinates[0][0]]} icon={customMarkerIcon} />
                <Marker position={[routeCoordinates[routeCoordinates.length - 1][1], routeCoordinates[routeCoordinates.length - 1][0]]} icon={customMarkerIcon} />
              </>
            )}
            {routeCoordinates.length > 0 && (
              <Polyline
                positions={routeCoordinates.map((c) => [c[1], c[0]])}
                color="#c41e3a"
                weight={4}
              />
            )}
            {startCountyData && <GeoJSON data={startCountyData} style={{ color: "#1a5f2e", weight: 3, fillColor: "#1a5f2e", fillOpacity: 0.2 }} />}
            {endCountyData && <GeoJSON data={endCountyData} style={{ color: "#c41e3a", weight: 3, fillColor: "#c41e3a", fillOpacity: 0.2 }} />}
            <MapView />
          </MapContainer>
        </div>
      )}
    </div>
  );
}

export default Calculator;
